import React from "react";
import ReactDOM from "react-dom/client";
import App from "@app/App";
import { resolveApiUrl } from "@shared/api/client";
import { ErrorBoundary } from "@shared/components/ErrorBoundary";
import "@/index.css";

// A stale lazy chunk after a deploy fails to load; fetch the fresh build once.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    const key = "app-chunk-reload-at";
    try {
      const last = Number(window.sessionStorage.getItem(key) ?? "0");
      if (Number.isFinite(last) && Date.now() - last < 10_000) {
        return;
      }
      window.sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // sessionStorage blocked — still worth one reload attempt.
    }
    window.location.reload();
  });
}

const appendResourceHint = ({
  rel,
  href,
  crossOrigin,
}: {
  rel: "preconnect" | "dns-prefetch";
  href: string;
  crossOrigin?: "anonymous";
}) => {
  const head = document.head;
  const selector = `link[rel="${rel}"][href="${href}"]`;
  if (head.querySelector(selector)) {
    return;
  }

  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (crossOrigin) {
    link.crossOrigin = crossOrigin;
  }
  head.appendChild(link);
};

const setupNetworkHints = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const apiOrigin = new URL(resolveApiUrl(), window.location.origin).origin;
  const origins = new Set<string>([
    apiOrigin,
    "https://www.google.com",
    "https://www.youtube.com",
    "https://i.ytimg.com",
  ]);

  origins.forEach((origin) => {
    appendResourceHint({
      rel: "dns-prefetch",
      href: origin,
    });
    appendResourceHint({
      rel: "preconnect",
      href: origin,
      crossOrigin: origin === window.location.origin ? undefined : "anonymous",
    });
  });
};

setupNetworkHints();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
